/**
 * Error thrown by BaseAPI for non-2xx responses.
 *
 * Previously BaseAPI threw the raw `Response`, which meant every consumer's
 * `error instanceof Error ? error.message : 'Failed to …'` fallback was always
 * false and the server's reason was discarded. ApiError carries the status and
 * the message the backend actually sent, so stores can surface it.
 *
 * Nest returns `{ statusCode, message: string | string[], error }`; Laravel
 * returns `{ message, errors }`. Both are handled by `messageFromBody`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  /** Parsed response body, when it was JSON. */
  readonly body: unknown;

  constructor(status: number, url: string, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.url = url;
    this.body = body;
  }
}

/**
 * Pull a human-readable message out of an error response body.
 * Returns null when the body carries nothing useful.
 */
export function messageFromBody(body: unknown): string | null {
  if (!body) return null;
  if (typeof body === 'string') return body.trim() || null;
  if (typeof body !== 'object') return null;

  const record = body as Record<string, unknown>;

  // Nest with stopAtFirstError still sends an array
  const message = record.message;
  if (Array.isArray(message)) {
    const first = message.find((entry) => typeof entry === 'string');
    if (first) return first as string;
  }
  if (typeof message === 'string' && message.trim()) {
    return message.trim();
  }

  // Laravel-style field errors: { errors: { field: ["msg"] } }
  const errors = record.errors;
  if (errors && typeof errors === 'object') {
    for (const value of Object.values(errors as Record<string, unknown>)) {
      if (Array.isArray(value) && typeof value[0] === 'string') {
        return value[0];
      }
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
  }

  if (typeof record.error === 'string' && record.error.trim()) {
    return record.error.trim();
  }

  return null;
}

/**
 * Build an ApiError from a failed Response, reading the body for a message.
 * Never throws — a body that cannot be read falls back to the status text.
 */
export async function apiErrorFromResponse(
  response: Response
): Promise<ApiError> {
  let body: unknown;
  let message: string | null = null;

  try {
    const text = await response.text();
    if (text) {
      try {
        body = JSON.parse(text);
        message = messageFromBody(body);
      } catch {
        body = text;
        // Don't surface an HTML error page as a message
        message = text.trimStart().startsWith('<') ? null : text.trim();
      }
    }
  } catch {
    // Body already consumed or stream failed - fall through to status text
  }

  return new ApiError(
    response.status,
    response.url,
    message || response.statusText || `Request failed with status ${response.status}`,
    body
  );
}
